// Chat — the shared type vocabulary for the conversation surface.
//
// This is the ONE place the load-bearing chat sums live; every Chat component
// imports from here so message-kind, proposal-summary op-counts, auto-run phase,
// drafting phase and plan-step are named once and reused. These are the UI's
// structural mirror of the conversation shapes — a presentation contract, not
// domain logic (@bilda/client imports zero @bilda/core). Messages, proposals and run
// state all arrive as props; nothing here computes, fetches, or mutates.
//
// Discipline (dev-ts): every varying shape is a named discriminated union with a
// `type` discriminant. Never a meaning-swapping boolean, never a raw object
// literal on a public surface. Each sum is drawn so the illegal states the docs
// call out cannot be constructed — see the notes on each.
//
// The GROUNDING vocabulary is NOT redefined here: the proposal card's grounding
// preview reuses `GroundingState` + `GroundingChip` from `../Grounding` so a
// proposal carries the SAME three-state evidence the editor inspects, unchanged
// (.claude/research/design/04). Grounding lives in a neutral home (owned by neither Chat nor
// Editor), so both surfaces import it as a peer — no Chat→Editor dependency.

import type { CitationLocator } from "../../app/conversationAdapter/citations.js";
import type { ExternalError } from "../../app/externalError.js";
import type { BuildState } from "../../app/materialBuildModel.js";
import type { ComposerResourceRef } from "../Composer/PowerComposer/types.js";
import type { GroundingState } from "../Grounding/types.js";

// Re-export so the Chat components read the structured-locator sum from ONE
// place (the Chat vocabulary) rather than reaching into the app adapter.
export type { CitationLocator };

// An opaque id, minted upstream (a conversation event id) and passed through
// unchanged. The UI never constructs one, so no brand/smart-ctor is needed — it
// is a pass-through label, not an invariant the presentation layer owns.
export type ChatEntryId = string;
export type PlanStepId = string;

// === Transcript entries (the message-kind sum) ================================
// A transcript is a flat list of these. Docs/design/03: one turn can be an
// author message, an agent message (markdown prose), or — the heart of the loop
// — a POINTER card summarising edits the agent landed in the document. Each is a
// dedicated named variant; `ChatEntryKind` is derived from the union, never
// hand-maintained. A proposal is a first-class entry, NOT a flag on an agent
// message, because it renders as its own artifact (ProposalCard) and carries a
// wholly different payload (a typed op-count summary + grounding), not prose.

export type AuthorEntry = {
    readonly type: "author";
    readonly id: ChatEntryId;
    // Plain text — the human's own words render pre-wrapped, never markdown.
    readonly text: string;
    // The resources the Author @-REFERENCED in this message (D4: the reference
    // relation, distinct from grounding). Rendered as static `ResourceMentionChip`
    // atoms on the sent bubble (north-star O3) — the chip IS this reference's
    // durable home; it does NOT appear in the Sources tier for a non-source kind.
    // Absent/empty ⇒ no chip row. `onRemove` is never wired here (a sent
    // reference is an immutable stamp, not a live control).
    readonly mentions?: readonly ComposerResourceRef[];
    // The same reference relation as reloaded from the durable message (its
    // `resourceRefs`): ids only, since the wire carries no kind or title.
    // A live entry carries `mentions` instead. Absent/empty ⇒ none.
    readonly referencedIds?: readonly string[];
    // When this entry was recorded, epoch ms (the event envelope's
    // `recordedAt`, projected in conversationAdapter). Optional: absent when the
    // source projection does not yet carry it (see the adapter's note), in which
    // case the message renders with no relative-time affordance.
    readonly timestamp?: number;
};

export type AgentEntry = {
    readonly type: "agent";
    readonly id: ChatEntryId;
    // Markdown source — the agent's voice renders through Prose (concise + warm,
    // per the agent-voice memory). Parsing is Prose's whole job.
    readonly markdown: string;
    // The flat `suggestedResponses` pill list (IDD adc80e66) is RETIRED (LANE
    // S3). A turn's hand-off is now the typed epilogue: an `ActionOfferEntry`
    // (transient, from the side-effect track) for concrete moves, or an
    // `InformationRequestEntry` (durable) for structured input. A plain reply
    // carries no move-ahead — the composer is the only affordance.
    // The last-applied streaming-delta `seq` (turnStream's per-message
    // idempotency/ordering cursor). Present ONLY while this bubble is being
    // assembled live from `text_delta` events — a delta whose `seq` is ≤ this
    // is a re-delivery and ignored, so smear/double-append is unrepresentable.
    // Absent on a durable reply loaded from the event log (streaming is over).
    readonly streamSeq?: number;
    // When this entry was recorded, epoch ms — same shape + optionality as
    // AuthorEntry.timestamp (projected in conversationAdapter from the envelope's
    // `recordedAt`). Drives the ticking relative-time label; absent ⇒ none.
    readonly timestamp?: number;
    // The källhänvisningar (source references) this message anchors (cite-B),
    // folded from the conversation's `citation_marker` artifacts in
    // conversationAdapter. ChatMessage renders them as inline numbered markers
    // plus an enriched "Läste källan" step. Absent/empty ⇒ no citation UI. Only
    // present on the DURABLE projection (a live streamed reply carries none until
    // the conversation reloads and its markers fold in).
    readonly citations?: readonly ChatCitation[];
};

// === Källhänvisning (a source reference the render consumes) ==================
// The transcript render's flat view of one cited part of one source (cite-B).
// It is the app adapter's `MessageCitation` (conversationAdapter/citations.ts)
// minus `messageId` (already grouped onto its owning AgentEntry) plus the
// conversation-global footnote `index` the adapter assigns as it lays the
// citations out. The footnote number is reused verbatim by the inline marker AND
// the ReferencesPanel row, so a reader matches one to the other by number even
// where the panel groups them out of sequence.
//
// `locator` stays STRUCTURED here (cite-A left it so on purpose): ChatMessage
// formats it to a Swedish string ("s. 12" / "Avsnitt: …") at the i18n edge. It
// is NON-optional — a källhänvisning always names a SPECIFIC part (owner
// refinement), so a whole-source reference (absent locator) is dropped in the
// adapter and never reaches this shape. `quote` is the optional verbatim
// excerpt — present only when the anchor carried a TextQuoteSelector.
export type ChatCitation = {
    readonly id: string;
    // 1-based, conversation-global footnote number.
    readonly index: number;
    // The deep-link target — the cited source's resource id.
    readonly resourceId: string;
    readonly sourceTitle: string;
    readonly locator: CitationLocator;
    readonly quote: string | undefined;
};

export type ProposalEntry = {
    readonly type: "proposal";
    readonly id: ChatEntryId;
    // The owning Proposal aggregate's id (from the domain `proposal_marker`).
    // Lets "Go to change" resolve the proposal's TARGET material and focus it,
    // even when the author drove the agent from another tab. Undefined only
    // when the source marker omitted it (legacy / mock fallback).
    readonly proposalId?: string;
    // The POINTER, not the payload (.claude/research/design/03): a typed op-count summary,
    // the agent's one-line rationale, and a grounding PREVIEW. The result itself
    // lives in the document; this card points at it.
    readonly summary: ProposalSummary;
    readonly rationale: string;
    readonly grounding: GroundingState;
    // Where it stands: waiting for you, or settled (then one line).
    readonly status: ProposalStatus;
    // The material the change targets, when known.
    readonly materialTitle?: string;
};

type ProposalPending = { readonly type: "pending" };
type ProposalAccepted = { readonly type: "accepted" };
type ProposalRejected = { readonly type: "rejected" };
export type ProposalStatus =
    | ProposalPending
    | ProposalAccepted
    | ProposalRejected;

export type PlanEntry = {
    readonly type: "plan";
    readonly id: ChatEntryId;
    readonly steps: readonly PlanStep[];
};

// === Material entry (a created-material pointer card) =========================
// chat-as-live-work-surface §Axis 2: a material the agent created via
// `create_material_from_source` renders as a first-class transcript CARD (title
// + kind + an "Open" action that focuses the material's tab), NOT a raw UUID in
// prose. Same FAMILY as ProposalEntry — a pointer, not the payload — but a
// distinct variant: a material is not a proposal (no op-count summary, no
// grounding preview, no review gate; it is an authored object you open), so it
// gets its own shape. Projected from the completed `create_material_from_source`
// tool result in conversationAdapter (the tool's output carries id + title +
// kind), never a new domain event.
//
// `kind` is a closed tag set mirroring the two authoring templates — `course`
// (T_pqbl_torus, the structured shape the editor renders) and `freeform`
// (T_freeform, unstructured notes). ChatMessage maps it exhaustively to a glyph
// + label, so a third template breaks the render until it earns one.
export type MaterialKind = "course" | "freeform";

export type MaterialEntry = {
    readonly type: "material";
    readonly id: ChatEntryId;
    // The created material's Resource id — the handle "Open" resolves to a tab.
    readonly materialId: string;
    // The material's display title (the card's headline). Named by the tool, so
    // the agent never has to speak the id.
    readonly title: string;
    readonly kind: MaterialKind;
    // Best-effort authored-item count ("· 3 questions"). Absent when the content
    // shape doesn't yield one (e.g. freeform) — the card then shows title + kind.
    readonly itemCount?: number;
};

// === Material BUILDING entry (the live half of the material card) =============
// material-streaming-2026-09-01 (TASK #28, the keystone's LIVENESS half): while
// a `create_material_from_source` tool call runs, the agent's build streams as a
// TRANSIENT card that fills in, then SETTLES into the durable `MaterialEntry`.
// This entry carries the `buildId`-keyed folded `BuildState` (materialBuildModel)
// — NOT a domain event: it is projected from the side-effect-track build deltas
// the turn stream relays (foldTurnStreamEvent), the same substrate the text-delta
// bubble rides. Its `id` is derived from the build id so the durable MaterialEntry
// (`${callId}-material`) SUPERSEDES it by id (replaceOrAppend / the load merge),
// exactly as the final message supersedes its streamed bubble — no double card.
export type MaterialBuildingEntry = {
    readonly type: "material_building";
    // `${buildId}-material` — the SAME id the durable MaterialEntry will carry,
    // so the settled pointer replaces this transient card by id.
    readonly id: ChatEntryId;
    // The folded live state (status "planning" | "building" | "stopped"). Once it
    // settles the durable MaterialEntry takes over, so "settled" is not rendered
    // from here.
    readonly build: BuildState;
    // The stamped elapsed read (the last applied delta's atMs) — the status
    // line's "0:14"; never a wall clock.
    readonly elapsedMs: number;
};

// === Export entry (a downloadable export pointer card) ========================
// F-E/Dm6 (dogfood-chat-2026-09-08): mirrors MaterialEntry's SHAPE, not its
// FAMILY — `export_package` hands back a download-ready artifact, so its card
// carries only the settled pointer (title + Open/Download), none of
// MaterialEntry's build machinery (an export never streams/builds; it either
// exists or it doesn't). A distinct variant, not a MaterialEntry with a flag,
// because the two entities differ in kind: a material is authored content you
// open and read; an export is a packaged deliverable you download. Projected
// from the completed `export_package` tool result in conversationAdapter,
// mirroring `materialCardFrom` exactly (same total/degrade-to-nothing posture).
//
// Title-only for the first cut (D-A05-3) — the tool's output also carries a
// target format, but reading it into a badge is deferred; add a `format` field
// here additively when that lands, rather than guessing the shape now.
export type ExportEntry = {
    readonly type: "export";
    readonly id: ChatEntryId;
    // The export's handle — reuses the packaged resource's id (see the tool's
    // header comment: exports have no distinct lifecycle/identity of their own
    // yet, a stated MVP shortcut). "Open"/"Download" resolve through this.
    readonly exportId: string;
    readonly title: string;
};

// === System entry (a turn that ended without an agent message) ================
// .claude/analysis/chat-implementation-design.md §0/§1.1: the domain records three
// CLOSING entries that carry no agent prose — `agent_interruption`, `agent_error`,
// `agent_budget_halt`. conversationAdapter DROPPED all three to `undefined`, so an
// interrupted / failed / cost-split turn rendered as a SILENT GAP. This variant is
// the slot those three land in: a quiet SYSTEM row (icon + word, not prose), named
// by OUTCOME, not a boolean on AgentEntry — it renders differently (a system note,
// not the agent's voice) and carries a reason, not markdown.
//
// alarm-discipline: a system row is a QUIET signal, not an alarm — colour is never
// the sole channel (an icon + a word carry the meaning), and it never steals focus.
//
// D18 + FR-2.5: every kind is one CAUSE of the same outcome (the turn was
// interrupted), rendered by ONE notice that says what happened, what is kept
// and what you can do (`interruptionCopy`). Kinds follow the design frames
// F-02..F-10; anything the boundary parsers do not recognise lands in
// `unknown` (carrying the raw cause), never a silent gap.

export type SystemEntryKind =
    | { readonly type: "interrupted" } // F-02: the author stopped the reply
    // F-05: the turn failed. `error` is present when the REQUEST was refused
    // (client-only, nothing recorded, the `toDisplay` text explains it);
    // absent for a durable agent error the server recorded.
    | { readonly type: "failed"; readonly error?: ExternalError }
    // F-07: the prompt outgrew the model's context window (O-4). NOT retryable
    // as-is (a retry re-sends the same oversized prompt).
    | { readonly type: "context_window_exceeded" }
    | { readonly type: "budget_halted" } // F-06: the turn was split to keep costs down
    // F-06: the assistant's account is out of credits. No recovery in the app
    // (an administrator adds more).
    | { readonly type: "budget_used_up" }
    // F-04: the connection dropped. `messageSaved`: the server recorded the
    // turn (bd-1wq, durable, survives a reload) vs. the request never arrived
    // (client-only, the message was not sent).
    | { readonly type: "connection_lost"; readonly messageSaved: boolean }
    // F-08 / D50: the log holds a turn that never reached a closing entry (the
    // tab closed or the server died mid-turn). PROJECTED from the log by
    // conversationAdapter, so it survives reloads; the chat module drops it
    // while this client is still driving that turn.
    | { readonly type: "unfinished" }
    // F-09: a colleague changed the same thing mid-turn. No signal yet (no
    // concurrent-edit detection); the kind and its copy exist so wiring it is
    // purely additive.
    | { readonly type: "colleague_clash" }
    | { readonly type: "signed_out" } // F-10: the session expired (401)
    // The designed default: a cause no parser recognises. `cause` is the raw
    // wire value, telemetry only, never shown.
    | { readonly type: "unknown"; readonly cause: string };

// F-03 is not a transcript entry: it is the LIVE state "a turn is running and
// the author has typed a new message", projected at the composer.
export type Interruption =
    | SystemEntryKind
    | { readonly type: "typing_while_working" };

// The notice's ONE primary action. The view maps each to a chat intent or a
// host request; a kind with no working recovery offers none.
export type InterruptionAction =
    | "continue"
    | "try_again"
    | "stop"
    | "new_conversation"
    | "sign_in";

export type SystemEntry = {
    readonly type: "system";
    readonly id: ChatEntryId;
    readonly kind: SystemEntryKind;
};

// === Structured-question surface (the form card) ==============================
// §1.1: the agent's structured question, rendered inline as a form CARD, and the
// author's typed answer resting in scrollback. These are the PRESENTATION MIRROR
// of the domain's `InformationRequest` / `FieldAnswer` (agent-forms.md §2) —
// @bilda/client imports zero @bilda/core, so the shapes are re-declared here as the UI's
// own vocabulary (same posture as `ProposalSummary`), fed by conversationAdapter.
//
// SLICE 0 establishes only the RENDER surface; the domain wiring + interactivity
// land in Slice 2. Every field/answer variant mirrors its domain sibling one-for-
// one so Slice 2 maps them without reshaping.

// A choice option pairs a stable value with a display label (mirror of the domain
// `ChoiceOption`). `value` is the key an answer references; `label` is what the
// reader sees.
export type ChatChoiceOption = {
    readonly value: string;
    readonly label: string;
    readonly help?: string;
};

// The closed field sum — one variant per domain `field_*` (agent-forms.md §2.2):
// free text, one-of, any-subset, and a bounded integer scale. `key` is the stable
// machine key correlating an answer back to its field (a pass-through label, like
// `ChatEntryId` — the UI never mints one, so no brand).
export type ChatTextField = {
    readonly type: "field_text";
    readonly key: string;
    readonly label: string;
    readonly help?: string;
    readonly placeholder?: string;
};
export type ChatSingleChoiceField = {
    readonly type: "field_single_choice";
    readonly key: string;
    readonly label: string;
    readonly help?: string;
    readonly options: readonly ChatChoiceOption[];
};
export type ChatMultiChoiceField = {
    readonly type: "field_multi_choice";
    readonly key: string;
    readonly label: string;
    readonly help?: string;
    readonly options: readonly ChatChoiceOption[];
};
export type ChatScaleField = {
    readonly type: "field_scale";
    readonly key: string;
    readonly label: string;
    readonly help?: string;
    readonly min: number;
    readonly max: number;
    readonly minLabel?: string;
    readonly maxLabel?: string;
    // One level per value, `min` first, each in words with a short meaning
    // (mirror of the domain `ScaleLevel`). Absent on older forms.
    readonly levels?: readonly ChatScaleLevel[];
};
export type ChatScaleLevel = {
    readonly label: string;
    readonly meaning?: string;
};
// D42: pick any of the library's sources. Carries no options: the card offers
// the library's current sources (`FormSourceOption[]`, projected in by the host).
type ChatSourcesField = {
    readonly type: "field_sources";
    readonly key: string;
    readonly label: string;
    readonly help?: string;
};
export type ChatField =
    | ChatTextField
    | ChatSingleChoiceField
    | ChatMultiChoiceField
    | ChatScaleField
    | ChatSourcesField;

// One library source a `field_sources` question offers.
export type FormSourceOption = {
    readonly id: string;
    readonly title: string;
};
export type ChatFieldKind = ChatField["type"];

// The answer sum mirrors the field sum one-for-one (agent-forms.md §2.4). Each
// carries the `key` it satisfies plus a per-variant value.
export type ChatTextAnswer = {
    readonly type: "answer_text";
    readonly key: string;
    readonly value: string;
};
export type ChatSingleChoiceAnswer = {
    readonly type: "answer_single_choice";
    readonly key: string;
    readonly value: string;
};
export type ChatMultiChoiceAnswer = {
    readonly type: "answer_multi_choice";
    readonly key: string;
    readonly values: readonly string[];
};
export type ChatScaleAnswer = {
    readonly type: "answer_scale";
    readonly key: string;
    readonly value: number;
};
// The picked sources, each with the title the Author saw (so the read-back and
// the agent never depend on the library still holding it).
type ChatSourcesAnswer = {
    readonly type: "answer_sources";
    readonly key: string;
    readonly sources: readonly FormSourceOption[];
};
export type ChatFieldAnswer =
    | ChatTextAnswer
    | ChatSingleChoiceAnswer
    | ChatMultiChoiceAnswer
    | ChatScaleAnswer
    | ChatSourcesAnswer;

// Where a request rests in its lifecycle. A named sum, not a boolean — `pending`
// awaits the author, `answered` collapsed to a read-only summary, `dismissed`
// collapsed to a "skipped" resting state.
export type InformationRequestStatus =
    | { readonly type: "pending" }
    | { readonly type: "answered" }
    | { readonly type: "dismissed" };

// One "screen" of the form — an ordered group of fields shown together in the
// stepped renderer. Mirror of the domain `Elicitation.ElicitationStep`. `key` is
// the stable correlation for progress + per-step validation (a pass-through, the
// UI never mints one). A flat form is the single-step case.
export type ChatStep = {
    readonly key: string;
    readonly title?: string;
    readonly note?: string;
    readonly fields: readonly ChatField[];
};

// The reserved key of the always-answerable FALLBACK field — the one free-text
// field a request degrades to when the agent's `_request_information` payload
// failed to parse into real fields (F5). It fires on BOTH sides of the boundary:
// the app bakes it into durable data (`epilogue.ts`) and the client mints it for
// a zero-field request (`conversationAdapter.fallbackChatField`). Its label +
// placeholder are the ONE piece of form copy the app authors (everything else the
// agent authors in the conversation's language), so they can't go through Lingui
// where they sit — durable domain data. Instead the CARD recognizes this key and
// renders a LOCALIZED label + placeholder at the edge, so no English leaks into a
// non-English UI. Kept in sync by string, not import, with `epilogue.ts`'s
// `FALLBACK_FIELD_KEY` (the two packages share no types).
export const FALLBACK_FIELD_KEY = "__fallback_response__";

// The reserved key of the always-present FREE-TEXT ESCAPE field. The agent's
// choice options are SUGGESTIONS, never a closed set the author is locked into —
// so a step that offers choices must ALSO offer a way to write your own answer
// (agent-forms doctrine: a free-text escape is a companion `field_text`, never a
// buried "other" option). The client GUARANTEES this at projection time
// (`conversationAdapter.projectRequestSteps`): any step with choices but no text
// field gets one appended under this key. Like `FALLBACK_FIELD_KEY`, its label +
// placeholder are synthesized OUTSIDE Lingui (a pure projection), so the CARD
// recognizes this key and renders a LOCALIZED label + placeholder at the edge —
// no English leaks into a non-English UI. Its answer round-trips as a plain
// `answer_text` (the domain does not require an answer's key to name a form
// field), and reads back as the author's own words.
export const OWN_ANSWER_FIELD_KEY = "__own_answer__";

export type InformationRequestEntry = {
    readonly type: "information_request";
    readonly id: ChatEntryId;
    readonly title: string;
    readonly note?: string;
    // Ordered steps (a flat form is one step). Answers still submit as ONE
    // command over all steps' fields — stepping is a render concern only.
    readonly steps: readonly ChatStep[];
    readonly status: InformationRequestStatus;
    // F113: once answered, the structured field→answer read-back NESTED on the
    // same entry, so the settled form and its answers render as ONE card (a
    // collapsible "Visa svar" disclosure) rather than two disconnected cards.
    // Absent while pending; resolved at projection time against this request's
    // own fields (`summarizeElicitationAnswers`). The domain event shape is
    // untouched — this is a projection-level fold of the answer artifact.
    readonly answers?: readonly AnsweredFieldSummary[];
};

// === Information request PENDING (the form-load placeholder) ==================
// F53: the agent is PREPARING a structured form. The vendor names the
// `_request_information` tool_use block seconds into generation, long before its
// form JSON finishes streaming — surfaced as a TRANSIENT turn-stream signal
// (`information_request_pending`, keyed by the round's message id). This entry is
// the DISTINCT form-load state it folds into: a form-shaped placeholder ("the
// form, loading"), NOT the generic working indicator that otherwise reads as a
// freeze. Transient like `action_offer` — never projected on reload; SUPERSEDED
// by the real `InformationRequestEntry` (same id) the instant the finished form
// arrives, and swept at turn end if the form never lands. Carries its id and the
// progress steps streamed so far (no fields to render yet).
export type InformationRequestPendingEntry = {
    readonly type: "information_request_pending";
    readonly id: ChatEntryId;
    // D30: the form's progress steps written so far, one per finished field
    // (agent-authored, in the conversation's language). Empty until the first.
    readonly parts: readonly string[];
};

// One answered field, resolved for the read-back summary (F97): the field's
// label paired with the chosen answer(s) as readable text (choice VALUES already
// mapped back to their option LABELS). `key` rides along so the answered-form
// card can LOCALIZE the reserved-key fields (the free-text escape / durable
// fallback) at the edge, exactly as the pending card does — no English leaks
// into a non-English UI. A pure projection built in `elicitationAnswerMapper`
// (`summarizeElicitationAnswers`); the domain event shape is untouched.
export type AnsweredFieldSummary = {
    readonly key: string;
    readonly label: string;
    readonly display: string;
};

// A DANGLING answer only: an `information_answer` whose `information_request`
// could not be resolved (no matching request in the accumulator). It degrades to
// a quiet "answered" line. A MATCHED answer never becomes this — its read-back is
// nested on the request entry's `answers` and renders inside the SAME card (F113).
type InformationSubmissionEntry = {
    readonly type: "information_submission";
    readonly id: ChatEntryId;
    // The `id` of the `InformationRequestEntry` this answers.
    readonly requestId: ChatEntryId;
    readonly answers: readonly ChatFieldAnswer[];
};

// === Action offer (the transient turn-epilogue hand-off) ======================
// LANE S3 (A-17): the agent's CONCRETE next moves at turn end — "Open the
// material", "Re-export as HTML", "Try again". The PRESENTATION MIRROR of the
// app-layer `ActionOffer` sum (@bilda/client imports zero @bilda/app, so it is
// re-declared here as the UI's own vocabulary), fed by `foldTurnStreamEvent`
// from the side-effect track — NOT a durable event, so it never reloads (an
// offer is a hint about the future, not a fact about the past). Each move is a
// dedicated named leaf with a `type` discriminant; the renderer maps it to an
// EXISTING command handler (`onOpenMaterial` / `onOpenExport` /
// `onDownloadExport`), or re-drives the agent (`onSuggestedResponse`) for a
// `send_prompt` move.
export type ChatOpenMaterialMove = {
    readonly type: "open_material";
    readonly label: string;
    readonly materialId: string;
};
export type ChatOpenExportMove = {
    readonly type: "open_export";
    readonly label: string;
    readonly exportId: string;
};
export type ChatDownloadExportMove = {
    readonly type: "download_export";
    readonly label: string;
    readonly exportId: string;
};
export type ChatSendPromptMove = {
    readonly type: "send_prompt";
    readonly label: string;
    readonly prompt: string;
};
// F9/F15: the actionable re-upload move — targets ONE Source whose stored
// content went missing. `sourceTitle` is optional context for the render's
// own copy; the wire's `label` rides along for shape parity with the other
// moves but the render authors its own canonical, localized button text
// (see `ActionOffer.tsx`) rather than trusting the agent's freeform phrasing
// for a move that always performs the exact same mechanical action.
type ChatRequestUploadMove = {
    readonly type: "request_upload";
    readonly label: string;
    readonly sourceId: string;
    readonly sourceTitle?: string;
};
export type ChatActionMove =
    | ChatOpenMaterialMove
    | ChatOpenExportMove
    | ChatDownloadExportMove
    | ChatSendPromptMove
    | ChatRequestUploadMove;

// `neutral` — a proactive next-step offer. `recovery` — the error hand-off
// register (rendered in a failure tone). A named sum, not a boolean.
export type ChatActionOfferTone = "neutral" | "recovery";

export type ActionOfferEntry = {
    readonly type: "action_offer";
    // The offer id (from the emitting turn's message id) — the fold keys the
    // tail entry on it so a re-published offer replaces rather than duplicates.
    readonly id: ChatEntryId;
    readonly tone: ChatActionOfferTone;
    // A non-empty list makes an empty offer unrepresentable.
    readonly moves: readonly [ChatActionMove, ...ChatActionMove[]];
};

// === Agent activity (the live status/tool-step trail) =========================
// .claude/analysis/chat-experience-audit.md P1.1 / perf-design-proposals.md §1: a
// turn is no longer a single blocking call rendered as a frozen dot — the
// agent's mid-turn TOOL mechanics (a tool call starting, that call's result)
// stream in as an ambient trail (alarm-discipline: this is ambient progress,
// not an alert). The trail carries TOOL mechanics ONLY — the agent's own
// preamble prose is an agent MESSAGE (O-7), not a dim step. Each step is its
// own named variant, not a flag: a `tool_running` step TRANSITIONS to
// `tool_done` when its matching result arrives — the transition is a distinct
// array element (a new step value), never a mutated boolean on the same one,
// keeping every intermediate state independently representable and replayable.

export type AgentActivityToolRunningStep = {
    readonly type: "tool_running";
    readonly id: string; // the domain's toolCallId — what a later result matches on
    readonly name: string; // the tool's name, e.g. "create_material_from_source"
};
export type AgentActivityToolDoneStep = {
    readonly type: "tool_done";
    readonly id: string;
    readonly name: string;
    readonly outcome: ToolStepOutcome;
};

// F124: what a finished call did — the UI mirror of the domain's
// `ToolCallDetail` (on `tool_call_completed`) and `ToolFailureReason` (on
// `tool_call_failed`), parsed at the boundary (conversationAdapter/
// toolCallDetail.ts). `detail` / `reason` are absent for legacy events and for
// tools with nothing to point at; the step then renders as before.
type ToolStepCompleted = {
    readonly type: "completed";
    readonly detail?: ToolCallDetail;
};
type ToolStepFailed = {
    readonly type: "failed";
    readonly reason?: ToolFailure;
};
export type ToolStepOutcome = ToolStepCompleted | ToolStepFailed;

// A dereferenceable resource the call named: `id` is the link target (never
// shown — F125), `title` is the call-time display snapshot.
export type ToolResourceKind =
    | "file"
    | "source"
    | "material"
    | "export"
    | "deleted";
export type ToolResourceLink = {
    readonly id: string;
    readonly kind: ToolResourceKind;
    readonly title: string;
};
// WHICH part of a source a read covered, inclusive: 1-based PDF pages, or the
// source's 1-based sections when it has no pages.
type ToolPageRange = {
    readonly type: "page_range";
    readonly first: number;
    readonly last: number;
};
type ToolSectionRange = {
    readonly type: "section_range";
    readonly first: number;
    readonly last: number;
};
export type ToolReadLocator = ToolPageRange | ToolSectionRange;
type ToolResourceTouched = {
    readonly type: "resource_touched";
    readonly resource: ToolResourceLink;
    // Absent on legacy calls and on reads of no specific part.
    readonly locator?: ToolReadLocator;
};
type ToolCitationAdded = {
    readonly type: "citation_added";
    readonly source: ToolResourceLink;
};
type ToolPackageExported = {
    readonly type: "package_exported";
    readonly package: ToolResourceLink;
};
type ToolResourcesListed = {
    readonly type: "resources_listed";
    readonly resources: readonly ToolResourceLink[];
};
// A read that found the source still being read in (PDF pages being
// transcribed) — not a failure; the row says so calmly.
type ToolSourceStillProcessing = {
    readonly type: "source_still_processing";
    readonly source: ToolResourceLink;
};
// A knowledge-bank claim the agent recorded: its wording and, per backing
// passage, the source (and page, when the source has pages).
type ToolClaimBacking = {
    readonly source: ToolResourceLink;
    readonly page?: number;
};
export type ToolRecordedClaim = {
    readonly type: "recorded_claim";
    readonly wording: string;
    readonly backing: readonly ToolClaimBacking[];
};
// `plan_parts` planned the parts of a material: the root and its new parts,
// reading order. Both are made by the call (bd-lya).
type ToolPartsPlanned = {
    readonly type: "parts_planned";
    readonly root: ToolResourceLink;
    readonly parts: readonly ToolResourceLink[];
};
export type ToolCallDetail =
    | ToolResourceTouched
    | ToolPartsPlanned
    | ToolCitationAdded
    | ToolPackageExported
    | ToolResourcesListed
    | ToolSourceStillProcessing
    | ToolRecordedClaim;

export type ToolFailureCategory =
    | "timeout"
    | "network"
    | "provider_busy"
    | "permission_denied"
    | "quota_exhausted"
    | "invalid_arguments"
    | "context_window_exceeded"
    | "source_content_missing"
    | "adapter_crashed"
    | "other";
type ToolUnknownFailure = { readonly type: "unknown_tool" };
type ToolAdapterFailure = {
    readonly type: "adapter_failed";
    readonly category: ToolFailureCategory;
    readonly detail?: string;
};
// A read hit a source whose stored content is gone, and the boundary could
// name WHICH source — so the row can offer "Upload again" into that very
// source. (A `source_content_missing` failure whose source couldn't be
// parsed stays an `adapter_failed`: same reason, no action.)
type ToolSourceContentMissing = {
    readonly type: "source_content_missing";
    readonly source: ToolResourceLink;
};
export type ToolFailure =
    | ToolUnknownFailure
    | ToolAdapterFailure
    | ToolSourceContentMissing;
export type AgentActivityStep =
    | AgentActivityToolRunningStep
    | AgentActivityToolDoneStep;

export type AgentActivityEntry = {
    readonly type: "agent_activity";
    readonly id: ChatEntryId;
    // Append-only for the turn's duration; the entry PERSISTS after the turn
    // closes (an auditable "what happened" trail), it is never removed.
    readonly steps: readonly AgentActivityStep[];
};

// === Unknown entry (the designed default) ====================================
// A log entry this version of the app has no rendering for (a newer server,
// an entry type added later). The adapter maps any unrecognised wire type
// here instead of dropping it, so the thread shows one generic line ("An
// update this version cannot show yet"), never a silent gap and never raw
// data. Carries only its id.
export type UnknownEntry = {
    readonly type: "unknown";
    readonly id: ChatEntryId;
};

export type ChatEntry =
    | AuthorEntry
    | AgentEntry
    | ProposalEntry
    | PlanEntry
    | MaterialEntry
    | MaterialBuildingEntry
    | ExportEntry
    | SystemEntry
    | InformationRequestEntry
    | InformationRequestPendingEntry
    | InformationSubmissionEntry
    | ActionOfferEntry
    | AgentActivityEntry
    | UnknownEntry;

// The discriminant set, derived from the union — the key space of ChatMessage's
// per-kind render. Adding a variant widens this and breaks the match until the
// new kind is handled (the point).
export type ChatEntryKind = ChatEntry["type"];

// === Proposal summary (typed op counts) =======================================
// Docs/design/03: the chat card summarises "1 added · 1 edited to Estates &
// taxation". Each counted op is its own named variant, NAMED BY OUTCOME (past
// tense, mirroring the material's diff vocabulary) — never one shape with a
// meaning-swapping `kind` string. `edited` alone carries a section target,
// because "edited to X" is the only phrase that names where the change landed;
// baking that field onto every variant would be a lie about add/remove/move.

export type AddedCount = { readonly type: "added"; readonly count: number };
export type EditedCount = {
    readonly type: "edited";
    readonly count: number;
    // The section the edits landed under — "…edited to Estates & taxation".
    // Optional: a bare "1 edited" is honest when no single section dominates.
    readonly target?: string;
};
export type RemovedCount = { readonly type: "removed"; readonly count: number };
export type MovedCount = { readonly type: "moved"; readonly count: number };

export type ProposalOpCount =
    | AddedCount
    | EditedCount
    | RemovedCount
    | MovedCount;

// A proposal always changes at least one thing — a non-empty list makes the
// "empty summary" state unrepresentable.
export type ProposalSummary = readonly [ProposalOpCount, ...ProposalOpCount[]];

// === Auto-run phase ===========================================================
// Docs/design/03: an autonomous run stays visible and interruptible — a running
// tally ("7 edits applied"), Pause/Stop, Undo last / Undo all this run. The
// phase is a discriminated sum so the AutoRunBar renders exactly the controls
// each phase permits (a match, never a flag): you cannot Pause a `done` run or
// show a tally on `idle`, because those states carry different data.
//   • idle    — armed, nothing applied yet (no tally, no interrupt controls)
//   • running — N applied so far; Pause / Stop / Undo available
//   • paused  — N applied, halted; Resume / Stop / Undo available
//   • done    — N applied, the run ended; only Undo remains

export type AutoRunPhase =
    | { readonly type: "idle" }
    | { readonly type: "running"; readonly applied: number }
    | { readonly type: "paused"; readonly applied: number }
    | { readonly type: "done"; readonly applied: number };

// === Drafting phase (the chat/composer-side status) ===========================
// Docs/design/03: while the agent works, the composer shows a quiet inline
// "Drafting…" (role=status); it RESOLVES when the edits land. Two states, a
// named sum (not a flag).
//
// An IN-DOCUMENT sibling (Editor/DraftingStatus, the typewriter-on-the-resource
// mark at the element) existed as a sketch and was deleted 2026-08-12 (orphan +
// name collision; see .claude/analysis/ui-design-synthesis.md §3). If an editor-side
// drafting surface returns and a third surface needs the phase shape, that is the
// moment to lift a shared `DraftingPhase` to a neutral home.

export type DraftingPhase =
    // The agent is working now; `activity` says on what (D51), absent until
    // the turn stream has said.
    | { readonly type: "drafting"; readonly activity?: LiveActivity }
    | { readonly type: "applied"; readonly count: number }; // resolved: N landed

// D51 — what the agent is doing RIGHT NOW, the UI mirror of `App.TurnActivity`
// (a transient turn-stream signal, never durable): planning its next step,
// writing its reply, or a tool call (composing or running), with the object it
// works on when known. The live row's label is a pure projection of it
// (`liveActivityMessage` in toolStepLabels.ts).
type LiveActivityThinking = { readonly type: "thinking" };
type LiveActivityWritingReply = { readonly type: "writing_reply" };
type LiveActivityTool = {
    readonly type: "tool";
    readonly toolName: string;
    readonly object?: string;
    // The kind of material a creating call makes, once its input says so.
    readonly materialKind?: MaterialKind;
};
export type LiveActivity =
    | LiveActivityThinking
    | LiveActivityWritingReply
    | LiveActivityTool;

// === Plan step (the editable pre-run plan) ====================================
// Docs/design/03: before an autonomous run the agent shows a short, EDITABLE
// plan — "tighten 3 stems · add 1 worked example · re-check grounding" — that
// the author edits or approves BEFORE any content is generated (approving the
// plan IS the consent for the run). Each step is a named variant discriminated
// by kind, because the kind is load-bearing, not cosmetic: `edit`/`add` are
// content-producing steps (they will land diffs), while `verify` is an
// evaluation-in-the-loop step (.claude/research/design/04: re-check grounding) that produces
// a measurement, not a diff. The kind picks the leading glyph AND marks whether
// the step yields reviewable changes.

export type EditStep = {
    readonly type: "edit";
    readonly id: PlanStepId;
    readonly description: string; // e.g. "tighten 3 stems"
};
export type AddStep = {
    readonly type: "add";
    readonly id: PlanStepId;
    readonly description: string; // e.g. "add 1 worked example"
};
export type VerifyStep = {
    readonly type: "verify";
    readonly id: PlanStepId;
    readonly description: string; // e.g. "re-check grounding"
};

export type PlanStep = EditStep | AddStep | VerifyStep;
export type PlanStepKind = PlanStep["type"];

// One learning context the "ground to…" menu can offer: the orchestrator's
// read model (id + title), not a domain ref.
export type SelectableContext = {
    readonly id: string;
    readonly title: string;
};

// The situation profile's view. The empty case is its own variant so a
// half-populated profile is unrepresentable. `descriptor` arrives
// PRE-COMPOSED by the app projection (one line of prose).
export type SituationProfileChipView =
    | { readonly type: "no_situation_profile" }
    | {
          readonly type: "situation_profile";
          readonly contextId: string;
          readonly title: string;
          readonly descriptor: string;
      };

// One node in the anchor's 1-hop bloom, already resolved to a displayable
// ref. `score` is the core `BloomScore` unbranded to a plain [0,1] number;
// `relation` is the raw relation name (e.g. "derives-from") it connects through.
export type AmbientContextItem = {
    readonly resourceRef: ComposerResourceRef;
    readonly score: number;
    readonly relation: string;
};
